import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Timer, Lock, Unlock, Pencil, ChevronDown, ChevronRight, X } from "lucide-react";

const iso = (d) => { const p = (n) => String(n).padStart(2, "0"); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`; };
const mondayOf = (d) => { const x = new Date(d); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };
const hrs = (m) => (Math.round(((m || 0) / 60) * 10) / 10) + "ω";
const dt = (s) => (s ? new Date(s).toLocaleString("el-GR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }) : "—");
const toLocalInput = (s) => { if (!s) return ""; const d = new Date(s); const p = (n) => String(n).padStart(2, "0"); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`; };

export default function Timesheets() {
  const qc = useQueryClient();
  const mon = mondayOf(new Date());
  const sun = new Date(mon); sun.setDate(sun.getDate() + 6);
  const [from, setFrom] = useState(iso(mon));
  const [to, setTo] = useState(iso(sun));
  const [open, setOpen] = useState({});
  const [edit, setEdit] = useState(null);

  const { data } = useQuery({ queryKey: ["tt-timesheets", from, to], queryFn: async () => (await api.get(`/team/timesheets?date_from=${from}&date_to=${to}`)).data });
  const rows = data?.rows || [];

  const refetch = () => qc.invalidateQueries({ queryKey: ["tt-timesheets"] });
  const saveEdit = useMutation({
    mutationFn: async (e) => (await api.put(`/team/timeclock/${e.id}`, { clock_in: e.clock_in, clock_out: e.clock_out, reason: e.reason })).data,
    onSuccess: () => { toast.success("Αποθηκεύτηκε"); setEdit(null); refetch(); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const lock = useMutation({
    mutationFn: async ({ member_id, unlock }) => (await api.post(`/team/timesheets/${unlock ? "unlock" : "approve"}`, { member_id, date_from: from, date_to: to })).data,
    onSuccess: (r) => { toast.success(r.locked != null ? `Κλειδώθηκαν ${r.locked}` : `Ξεκλειδώθηκαν ${r.unlocked}`); refetch(); },
    onError: (e) => toast.error(apiErr(e)),
  });

  return (
    <div className="space-y-5" data-testid="timesheets-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">Team &amp; Time</p>
          <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1 inline-flex items-center gap-2"><Timer className="h-7 w-7" /> Timesheets</h1>
        </div>
        <div className="flex items-center gap-2">
          <input data-testid="ts-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-xl border border-[#E3DED4] px-3 py-2 text-sm" />
          <span className="text-[#8A8577]">–</span>
          <input data-testid="ts-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-xl border border-[#E3DED4] px-3 py-2 text-sm" />
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#45423C] text-left text-[10px] uppercase tracking-wider text-white/80">
              <th className="px-4 py-2.5">Μέλος</th>
              <th className="px-3 py-2.5 text-right">Ωρολόγιο</th>
              <th className="px-3 py-2.5 text-right">Κανονικές</th>
              <th className="px-3 py-2.5 text-right">Υπερωρίες</th>
              <th className="px-3 py-2.5 text-right">Εργασίες πεδίου</th>
              <th className="px-3 py-2.5 text-right">Σύνολο</th>
              {data?.can_cost && <th className="px-3 py-2.5 text-right">Κόστος</th>}
              <th className="px-3 py-2.5 text-right">Ενέργειες</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <React.Fragment key={r.member_id}>
                <tr className="border-t border-[#E3DED4] hover:bg-[#FAF8F4]" data-testid={`ts-row-${r.member_id}`}>
                  <td className="px-4 py-2.5">
                    <button onClick={() => setOpen({ ...open, [r.member_id]: !open[r.member_id] })} className="inline-flex items-center gap-1.5 font-medium text-[#45423C]">
                      {open[r.member_id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />} {r.member_name}
                      {r.locked && <Lock className="h-3.5 w-3.5 text-[#9A6A3A]" />}
                    </button>
                  </td>
                  <td className="px-3 py-2.5 text-right tabular">{hrs(r.worked_min)}</td>
                  <td className="px-3 py-2.5 text-right tabular text-[#6F6A60]">{hrs(r.regular_min)}</td>
                  <td data-testid={`ts-ot-${r.member_id}`} className={"px-3 py-2.5 text-right tabular " + ((r.overtime_min || 0) > 0 ? "font-semibold text-[#C86235]" : "text-[#8A8577]")}>{hrs(r.overtime_min)}</td>
                  <td className="px-3 py-2.5 text-right tabular text-[#6E7A63]">{hrs(r.fieldops_min)}</td>
                  <td className="px-3 py-2.5 text-right tabular font-semibold">{hrs(r.total_min)}</td>
                  {data?.can_cost && <td className="px-3 py-2.5 text-right tabular">{r.cost != null ? "€" + r.cost : "—"}</td>}
                  <td className="px-3 py-2.5 text-right">
                    {data?.can_lock && (r.locked
                      ? <button data-testid={`ts-unlock-${r.member_id}`} onClick={() => lock.mutate({ member_id: r.member_id, unlock: true })} className="text-xs inline-flex items-center gap-1 text-[#6E7A63]"><Unlock className="h-3.5 w-3.5" /> Ξεκλείδωμα</button>
                      : <button data-testid={`ts-approve-${r.member_id}`} onClick={() => lock.mutate({ member_id: r.member_id })} className="text-xs inline-flex items-center gap-1 text-[#45423C] font-semibold"><Lock className="h-3.5 w-3.5" /> Έγκριση</button>)}
                  </td>
                </tr>
                {open[r.member_id] && r.entries.map((e) => (
                  <tr key={e.id} className="bg-[#FAF8F4] text-xs text-[#6F6A60]" data-testid={`ts-entry-${e.id}`}>
                    <td className="px-4 py-2 pl-10" colSpan={2}>{dt(e.clock_in)} → {dt(e.clock_out)} {e.edited && <span className="text-[#9A6A3A]">(επεξ.)</span>} {e.status === "open" && <span className="text-[#3F6B4C]">σε εξέλιξη</span>}</td>
                    <td className="px-3 py-2 text-right" colSpan={data?.can_cost ? 3 : 2}>{e.source === "kiosk" ? "Kiosk" : "Web"} · {hrs(e.worked_min)}</td>
                    <td className="px-3 py-2 text-right">
                      {!r.locked && <button data-testid={`ts-edit-${e.id}`} onClick={() => setEdit({ id: e.id, clock_in: toLocalInput(e.clock_in), clock_out: toLocalInput(e.clock_out), reason: "" })} className="text-[#45423C]"><Pencil className="h-3.5 w-3.5" /></button>}
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-[#6F6A60]">Καμία καταγραφή για αυτό το διάστημα.</td></tr>}
          </tbody>
        </table>
      </div>

      {edit && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" data-testid="ts-edit-modal">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4"><h2 className="font-serif text-xl text-[#45423C]">Επεξεργασία κάρτας</h2><button onClick={() => setEdit(null)}><X className="h-5 w-5 text-[#6F6A60]" /></button></div>
            <div className="space-y-3 text-sm">
              <div><label className="text-[11px] uppercase tracking-wider text-[#6F6A60] font-semibold">Έναρξη</label><input data-testid="ts-edit-in" type="datetime-local" value={edit.clock_in} onChange={(e) => setEdit({ ...edit, clock_in: e.target.value })} className="mt-1 w-full rounded-xl border border-[#E3DED4] px-3 py-2" /></div>
              <div><label className="text-[11px] uppercase tracking-wider text-[#6F6A60] font-semibold">Λήξη</label><input data-testid="ts-edit-out" type="datetime-local" value={edit.clock_out} onChange={(e) => setEdit({ ...edit, clock_out: e.target.value })} className="mt-1 w-full rounded-xl border border-[#E3DED4] px-3 py-2" /></div>
              <div><label className="text-[11px] uppercase tracking-wider text-[#6F6A60] font-semibold">Αιτιολογία (υποχρεωτική)</label><input data-testid="ts-edit-reason" value={edit.reason} onChange={(e) => setEdit({ ...edit, reason: e.target.value })} className="mt-1 w-full rounded-xl border border-[#E3DED4] px-3 py-2" /></div>
            </div>
            <div className="mt-6 flex gap-3">
              <button data-testid="ts-edit-save" disabled={!edit.reason || saveEdit.isPending} onClick={() => saveEdit.mutate({ ...edit, clock_in: edit.clock_in ? new Date(edit.clock_in).toISOString() : null, clock_out: edit.clock_out ? new Date(edit.clock_out).toISOString() : null })} className="flex-1 rounded-xl bg-[#45423C] text-white py-2.5 text-sm font-semibold disabled:opacity-50">Αποθήκευση</button>
              <button onClick={() => setEdit(null)} className="rounded-xl border border-[#E3DED4] px-4 py-2.5 text-sm">Άκυρο</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
